import { LocalDataError } from '../types.js';
import { assertValidDeviceCode } from '../domain/context.js';
import { isUuid } from '../domain/ids.js';
import { nowIso } from '../domain/time.js';
import { migrate, validateSchema } from './migrations.js';
import { CatalogRepository } from '../repos/catalog.js';
import { TablesRepository } from '../repos/tables.js';
import { StaysRepository } from '../repos/stays.js';
import { OfferRepository } from '../repos/offers.js';
import { OrdersRepository } from '../repos/orders.js';
import { InvoicesRepository } from '../repos/invoices.js';
import { SequenceRepository } from '../repos/sequences.js';
import { SessionRepository } from '../repos/sessions.js';
import { ConfigRepository } from '../repos/config.js';
import { AuditRepository } from '../repos/audit.js';
import { OfflineAuthRepository } from '../auth/offlineAuth.js';
import { SyncQueueRepository } from '../sync/queue.js';
import { SyncStateRepository } from '../sync/state.js';
import { ConflictRepository } from '../sync/conflicts.js';
import { TombstoneRepository } from '../sync/tombstones.js';
export class LocalDatabase {
    driver;
    context;
    migration;
    catalog;
    tables;
    stays;
    offers;
    orders;
    invoices;
    sequences;
    sessions;
    config;
    audit;
    /** Phase 8 offline sign-in (PIN + capability grant + local_session). */
    auth;
    syncQueue;
    syncState;
    conflicts;
    tombstones;
    constructor(driver, context, migration) {
        this.driver = driver;
        this.context = context;
        this.migration = migration;
        this.catalog = new CatalogRepository(driver);
        this.tables = new TablesRepository(driver);
        this.stays = new StaysRepository(driver);
        this.offers = new OfferRepository(driver);
        this.sequences = new SequenceRepository(driver, context);
        this.audit = new AuditRepository(driver);
        this.syncQueue = new SyncQueueRepository(driver, context);
        this.syncState = new SyncStateRepository(driver);
        this.conflicts = new ConflictRepository(driver);
        this.tombstones = new TombstoneRepository(driver);
        this.orders = new OrdersRepository(driver, context, this.sequences, this.audit, this.offers);
        this.invoices = new InvoicesRepository(driver, context);
        this.sessions = new SessionRepository(driver);
        this.config = new ConfigRepository(driver);
        this.auth = new OfflineAuthRepository(driver);
    }
    /**
     * Open (and if needed provision or upgrade) the database behind `driver`
     * for `context`. Nothing else in the data layer may be used before this
     * resolves - repositories handed out by the returned instance are only
     * constructed once the schema is validated.
     */
    static async open(driver, context, options = {}) {
        try {
            return await LocalDatabase.openInner(driver, context, options);
        }
        catch (error) {
            // A refused open must not leak the connection: on Windows an open
            // handle blocks even deleting the file, and on the kiosk it would pin
            // the native store to a database we refused to use.
            try {
                await driver.close();
            }
            catch {
                // The close is best-effort; the original error is what matters.
            }
            throw error;
        }
    }
    /**
     * Open an ALREADY-PROVISIONED database, deriving the context from its own
     * `local_device` row. This is the kiosk boot path: the file is the durable
     * record of the device binding, so a wiped WebView localStorage cannot
     * orphan it. Fails with NOT_PROVISIONED on a fresh file - enrolment must
     * run `open()` with an explicit context first.
     */
    static async resume(driver, options = {}) {
        try {
            await migrate(driver);
            const rows = await driver.query({ sql: 'SELECT * FROM local_device LIMIT 1' });
            const existing = rows[0];
            if (!existing) {
                throw new LocalDataError('NOT_PROVISIONED', 'This device has not been provisioned yet');
            }
            const context = {
                deviceId: String(existing.id),
                deviceCode: String(existing.device_code),
                tenantId: String(existing.tenant_id),
                propertyId: String(existing.property_id),
            };
            return await LocalDatabase.openInner(driver, context, options);
        }
        catch (error) {
            try {
                await driver.close();
            }
            catch {
                // Best-effort; the original error is what matters.
            }
            throw error;
        }
    }
    static async openInner(driver, context, options) {
        validateContext(context);
        const migration = await migrate(driver);
        await validateSchema(driver);
        const rows = await driver.query({ sql: 'SELECT * FROM local_device LIMIT 1' });
        const existing = rows[0];
        if (!existing) {
            await driver.execute({
                sql: `INSERT INTO local_device
                (id, device_code, tenant_id, property_id, server_url, app_version, enrolled_at, created_at)
              VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
                params: [
                    context.deviceId,
                    context.deviceCode,
                    context.tenantId,
                    context.propertyId,
                    options.serverUrl ?? '',
                    options.appVersion ?? '',
                    nowIso(),
                    nowIso(),
                ],
            });
        }
        else {
            const mismatch = String(existing.id) !== context.deviceId ||
                String(existing.tenant_id) !== context.tenantId ||
                String(existing.property_id) !== context.propertyId;
            if (mismatch) {
                throw new LocalDataError('DEVICE_CONTEXT_MISMATCH', 'This database file belongs to a different device/tenant/property binding. ' +
                    'Refusing to open it with the supplied context.');
            }
            if (options.appVersion && options.appVersion !== String(existing.app_version)) {
                await driver.execute({
                    sql: 'UPDATE local_device SET app_version = ?',
                    params: [options.appVersion],
                });
            }
        }
        return new LocalDatabase(driver, context, migration);
    }
    close() {
        return this.driver.close();
    }
}
function validateContext(context) {
    if (!isUuid(context.deviceId)) {
        throw new LocalDataError('INVALID_CONTEXT', `deviceId must be a UUID (got "${context.deviceId}")`);
    }
    if (!isUuid(context.tenantId) || !isUuid(context.propertyId)) {
        throw new LocalDataError('INVALID_CONTEXT', 'tenantId and propertyId must be UUIDs');
    }
    assertValidDeviceCode(context.deviceCode);
}
//# sourceMappingURL=database.js.map